import React from "react";
import SafeLink from "components/commons/SafeLink";
import styled from "styled-components";

import {Spinner} from "components";
import {CountryDropdown} from "components/NewPartners/commons";
import {type, typeMobile} from "components/HomePagePartner/typography";
import {WealthyEnv} from "helpers";
import {colors} from "nxtconstants";

const downArrow = `${WealthyEnv.HOMEPAGE_CDN_URL}/partners-dropdown-arrow.svg`;
const partnersPrivacyUrl = `${WealthyEnv.URL}/partners-privacy`;
const termsUrl = `${WealthyEnv.URL}/terms-and-conditions`;

const PartnerHomeForm = ({
  resendOtp,
  isLoading,
  isPartnerExisting,
  submitForm,
  formData,
  handlePaste,
  onChangeHandler,
  step,
  errorText,
  onKeyUpHandler,
  counter,
  selectedCountryCode,
  showDropdown,
  setShowDropdown,
  handleCountryCodeChange,
  onOtpChangeHandler
}) => {
  const otpDigits = [...Array(5).keys()];

  const otpContent = otpDigits.map((index) => (
    <OtpInput
      autoFocus={index === 0}
      className="otp-input"
      data-index={index}
      id={index}
      key={index}
      maxLength="1"
      placeholder="_"
      type="tel"
      onChange={onOtpChangeHandler}
      onKeyUp={(event) => onKeyUpHandler(event.keyCode, index - 1, event.target, index + 1)}
      onPaste={handlePaste}
    />
  ));

  return (
    <FormShell>
      <FormHeader>
        <div>
          {step === 1 ? (
            <>
              <FormTitle>Sign Up Now!</FormTitle>
              <FormSubtitle>No setup fee. No lock-in.</FormSubtitle>
            </>
          ) : (
            <FormTitle>Verify your number</FormTitle>
          )}
        </div>
      </FormHeader>

      {step === 1 ? (
        <>
          <FormRow>
            <Field
              name="firstName"
              placeholder="First name"
              type="text"
              value={formData.firstName}
              onChange={onChangeHandler}
            />
            <Field
              name="lastName"
              placeholder="Last name"
              type="text"
              value={formData.lastName}
              onChange={onChangeHandler}
            />
          </FormRow>

          <FullField
            name="email"
            placeholder="Email"
            type="text"
            value={formData.email}
            onChange={onChangeHandler}
          />
          {errorText.email ? <ErrorText>{errorText.email}</ErrorText> : null}

          <PhoneRow>
            <CountryTrigger onClick={() => setShowDropdown(!showDropdown)} type="button">
              <CountryCode>{selectedCountryCode.code}</CountryCode>
              <CountryDropdown
                callback={handleCountryCodeChange}
                selectedItem={selectedCountryCode.code}
                setShowDropdown={setShowDropdown}
                showDropdown={showDropdown}
              />
              <DropdownArrow $open={showDropdown} alt="" src={downArrow} />
            </CountryTrigger>
            <PhoneField
              name="phoneNumber"
              placeholder="Phone number"
              type="number"
              value={formData.phoneNumber}
              onChange={onChangeHandler}
            />
          </PhoneRow>
          {errorText.phoneNumber ? <ErrorText>{errorText.phoneNumber}</ErrorText> : null}

          {errorText.captcha ? <ErrorText>{errorText.captcha}</ErrorText> : null}

          <SubmitButton type="button" onClick={submitForm}>
            {isLoading ? (
              <Spinner color="#fff" size="20px" />
            ) : (
              <>
                Become a Wealthy Partner
                <ButtonArrow aria-hidden="true">→</ButtonArrow>
              </>
            )}
          </SubmitButton>
            <Divider />
          <PolicyText>
            By joining, you agree to Wealthy&apos;s{" "}
            <SafeLink href={partnersPrivacyUrl} rel="noopener noreferrer" target="_blank">
              Privacy Policy
            </SafeLink>{" "}
            and{" "}
            <SafeLink href={termsUrl} rel="noopener noreferrer" target="_blank">
              Terms of Service
            </SafeLink>
            .
          </PolicyText>
        </>
      ) : (
        <OtpBlock>
          <OtpDescription>
            {isPartnerExisting ? (
              <>
                This number {selectedCountryCode.code} {formData.phoneNumber} is already linked to an
                existing account. Enter the OTP sent to your number to login.
              </>
            ) : (
              <>
                Enter the OTP sent to {selectedCountryCode.code} {formData.phoneNumber} to verify your
                number.
              </>
            )}
          </OtpDescription>

          <OtpInputs>
            {isLoading ? <Spinner color={colors.primary} size="20px" /> : otpContent}
          </OtpInputs>

          <ResendRow>
            Didn&apos;t receive the OTP?{" "}
            {counter === 0 ? (
              <ResendButton type="button" onClick={resendOtp}>
                Resend
              </ResendButton>
            ) : (
              <ResendTimer>You can resend it in: {counter} sec</ResendTimer>
            )}
          </ResendRow>
        </OtpBlock>
      )}
    </FormShell>
  );
};

const FormShell = styled.div`
  width: 100%;
  min-height: 38rem;
  display: flex;
  flex-direction: column;

`;

const FormHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.6rem;
  margin-bottom: 2rem;
`;

const FormTitle = styled.h3`
  font-family: "DM Serif Display";
  font-size: ${type.cardHeader};
  font-weight: 400;
  line-height: 1.2;
  color: ${colors.textBlack};
  margin: 0 0 0.4rem;

  @media (max-width: 768px) {
    font-size: ${typeMobile.cardHeader};
  }
`;

const FormSubtitle = styled.p`
  font-family: Lato;
  font-size: ${type.bodySmall};
  font-weight: 400;
  line-height: 1.4;
  color: ${colors.textGray};
  margin: 0;

  @media (max-width: 768px) {
    font-size: ${typeMobile.bodySmall};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.2rem;
  margin-bottom: 1.2rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.input`
  width: 100%;
  height: 4.8rem;
  padding: 0 1.6rem;
  border: 1px solid ${colors.lightGray};
  border-radius: 1.2rem;
  background: ${colors.white};
  font-family: Lato;
  font-size: ${type.bodySmall};
  color: ${colors.textBlack};
  outline: none;
  box-sizing: border-box;

  @media (max-width: 768px) {
    font-size: ${typeMobile.body};
  }

  &::placeholder {
    color: ${colors.dustyGray};
  }

  &:focus {
    border-color: ${colors.primary};
  }
`;

const FullField = styled(Field)`
  margin-bottom: 1.2rem;
`;

const PhoneRow = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 1.2rem;
  position: relative;
`;

const CountryTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  height: 4.8rem;
  padding: 0 1.2rem;
  border: 1px solid ${colors.lightGray};
  border-right: none;
  border-radius: 1.2rem 0 0 1.2rem;
  background: ${colors.white};
  cursor: pointer;
  position: relative;
  font-family: Lato;
  font-size: ${type.bodySmall};
  color: ${colors.textBlack};

  @media (max-width: 768px) {
    font-size: ${typeMobile.body};
  }
`;

const CountryCode = styled.span``;

const DropdownArrow = styled.img`
  width: 1rem;
  transition: transform 0.2s ease;
  transform: ${({$open}) => ($open ? "rotate(180deg)" : "rotate(0deg)")};
`;

const PhoneField = styled.input`
  flex: 1;
  min-width: 0;
  height: 4.8rem;
  padding: 0 1.6rem;
  border: 1px solid ${colors.lightGray};
  border-radius: 0 1.2rem 1.2rem 0;
  background: ${colors.white};
  font-family: Lato;
  font-size: ${type.bodySmall};
  color: ${colors.textBlack};
  outline: none;
  box-sizing: border-box;

  @media (max-width: 768px) {
    font-size: ${typeMobile.body};
  }

  &::placeholder {
    color: ${colors.dustyGray};
  }

  &:focus {
    border-color: ${colors.primary};
  }
`;

const ErrorText = styled.span`
  display: block;
  font-family: Lato;
  font-size: ${type.bodySmall};
  color: #d14343;
  margin: -0.4rem 0 1rem;

  @media (max-width: 768px) {
    font-size: ${typeMobile.bodySmall};
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 4.8rem;
  margin-top: 0.4rem;
  border: none;
  border-radius: 4rem;
  background: ${colors.primary};
  color: ${colors.white};
  font-family: Lato;
  font-size: ${type.bodySmall};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }

  &:hover {
    opacity: 0.92;
  }
`;

const ButtonArrow = styled.span`
  margin-left: 0.4rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid #d8d8d8;
  margin: 20px 0 20px;
`;

const PolicyText = styled.p`
  font-family: Lato;
  font-size: 1.4rem;
  line-height: 1.5;
  text-align: center;
  color: ${colors.textGray};

  @media (max-width: 768px) {
    font-size: ${typeMobile.body};
  }

  a {
    font-family: inherit;
    font-size: 1.2rem;
    color: ${colors.primary};
    text-decoration: underline;

    @media (max-width: 768px) {
      font-size: ${typeMobile.body};
    }
  }
`;

const OtpBlock = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const OtpDescription = styled.p`
  font-family: Lato;
  font-size: ${type.bodySmall};
  line-height: 1.6;
  color: ${colors.textGray};
  text-align: center;
  margin: 0 0 2rem;

  @media (max-width: 768px) {
    font-size: ${typeMobile.bodySmall};
  }
`;

const OtpInputs = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  width: 100%;
  margin-bottom: 1.6rem;
`;

const OtpInput = styled.input`
  width: 4.4rem;
  height: 4.8rem;
  border: 1px solid ${colors.lightGray};
  border-radius: 1rem;
  background: ${colors.white};
  text-align: center;
  font-family: Lato;
  font-size: ${type.body};
  color: ${colors.textBlack};
  outline: none;

  @media (max-width: 768px) {
    font-size: ${typeMobile.body};
  }

  &:focus {
    border-color: ${colors.primary};
  }
`;

const ResendRow = styled.p`
  font-family: Lato;
  font-size: ${type.bodySmall};
  color: ${colors.textGray};
  margin: 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: ${typeMobile.bodySmall};
  }
`;

const ResendButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-family: Lato;
  font-size: ${type.bodySmall};
  font-weight: 600;
  color: ${colors.primary};
  cursor: pointer;

  @media (max-width: 768px) {
    font-size: ${typeMobile.bodySmall};
  }
`;

const ResendTimer = styled.span`
  font-weight: 500;
  color: ${colors.textBlack};
`;

export default PartnerHomeForm;
